import { Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiVersions } from 'src/app/shared/services/api-version';
import { BaseService } from 'src/app/shared/services/base.service';
import { Person } from '../models/person';
import { TeamHistory } from '../models/teamHistory';
import { Image } from '../models/image';
import { Note } from 'src/app/people/models/note';
import { PersonEvaluationSummary } from 'src/app/shared/models/evaluation/personEvaluationSummary';
import { Education } from '../models/education';
import { PersonCertification } from '../models/personCertification';
import {
  PersonCertificationComparator,
  StringIgnoreCaseComparator
} from 'src/app/shared/utils/comparators';
import { PersonApplication } from 'src/app/shared/models/application/personApplication';
import { PersonEvaluationResponsesDetail } from 'src/app/shared/models/evaluation/personEvaluationResponsesDetail';
import { EvaluationFilterResult } from 'src/app/shared/models/evaluation/evaluationFilterResult';
import { IEvaluationQueryModel } from 'src/app/shared/models/evaluation/evaluationQueryModel';
import { nameOf } from 'src/app/shared/utils/name-of';
import { PersonCertificationWrite } from '../models/personCertificationWrite';
import { Certification } from '../models/certification';

@Injectable({
  providedIn: 'root'
})
export class PersonProfileService extends BaseService {
  private baseUsersPath: string = 'users';

  public readonly person = new BehaviorSubject<Person>(null);
  public readonly forms = new BehaviorSubject<PersonApplication[]>([]);
  public readonly personTeamHistory = new BehaviorSubject<TeamHistory[]>(null);
  public readonly personEvaluationSummary = new BehaviorSubject<PersonEvaluationSummary[]>([]);
  public readonly teamEvaluationResponsesAggregate = new Subject<PersonEvaluationResponsesDetail>();
  public readonly educations = new BehaviorSubject<Education[]>([]);
  public readonly personCertifications = new BehaviorSubject<PersonCertification[]>([]);
  public readonly personStatus = new BehaviorSubject<string>(null);

  constructor() {
    super();
  }

  private baseCertificationsPath: string = 'certifications';

  private usersBaseUrl(userId: string) {
    return `${this.baseUsersPath}/${userId}`;
  }

  private getPersonTeamHistoryUrl(userId: string) {
    return this.usersBaseUrl(userId).concat('/teams');
  }

  private getEvaluationsSummaryUrl(userId: string) {
    return this.usersBaseUrl(userId).concat('/evaluations');
  }

  private getPersonImageUrl(userId: string) {
    return this.usersBaseUrl(userId).concat('/image');
  }

  private getPersonEducationUrl(userId: string) {
    return this.usersBaseUrl(userId).concat('/educations');
  }

  private getPersonCertificationsUrl(userId: string) {
    return this.usersBaseUrl(userId).concat('/certifications');
  }

  private getApplicationsUrl(userId: string) {
    return this.usersBaseUrl(userId).concat('/applications');
  }

  private convertApplications(data): PersonApplication[] {
    return data.map((application) => PersonApplication.parse(application));
  }

  private convertPerson(data): Person {
    if (!data) return null;
    return new Person(data);
  }

  private convertTeamHistoryData(data): TeamHistory[] {
    return data.map((teamHistory) => new TeamHistory(teamHistory));
  }

  private convertEvaluationsSummaryData(data): PersonEvaluationSummary[] {
    return data.map((evaluationSummary) => PersonEvaluationSummary.parse(evaluationSummary));
  }

  private convertEducationData(data): Education[] {
    return data.map((education) => new Education(education));
  }

  private convertPersonCertificationsData(data): PersonCertification[] {
    return data.map((personCertification) => new PersonCertification(personCertification));
  }

  private orderPersonCertifications(data): PersonCertification[] {
    return data.sort(PersonCertificationComparator);
  }

  private convertEvaluationResponseDetailData(data) {
    if (data) {
      let evaluationODataResult = new EvaluationFilterResult().parse(data);
      return PersonEvaluationResponsesDetail.parse(evaluationODataResult);
    }
  }

  private getPersonRatingUrl(userId: string) {
    return this.usersBaseUrl(userId).concat('/update-rating');
  }

  private getTeamEvaluationResponsesDetailUrl(userId: string, teamId: string) {
    const filter = `$filter=${nameOf<IEvaluationQueryModel>(
      (e) => e.evaluateeId
    )} eq ${userId} and ${nameOf<IEvaluationQueryModel>(
      (e) => e.teamId
    )} eq ${teamId} and responses/any()`;
    const select = `$select=${nameOf<IEvaluationQueryModel>(
      (e) => e.teamName
    )}, ${nameOf<IEvaluationQueryModel>(
      (e) => e.evaluateePosition
    )}, ${nameOf<IEvaluationQueryModel>((e) => e.evaluationName)}, ${nameOf<IEvaluationQueryModel>(
      (e) => e.evaluationResponseDate
    )}`;
    const expand = `$expand=${nameOf<IEvaluationQueryModel>((e) => e.responses)}`;
    return `query/evaluationMaxResult?${filter}&${select}&${expand}&$count=true`;
  }

  private getEvaluationResponsesDetailUrl(userId: string, evaluationFormId) {
    const filter = `$filter=${nameOf<IEvaluationQueryModel>(
      (e) => e.evaluateeId
    )} eq ${userId} and ${nameOf<IEvaluationQueryModel>(
      (e) => e.evaluationFormId
    )} eq ${evaluationFormId} and ${nameOf<IEvaluationQueryModel>((e) => e.responses)}/any()`;
    const select = `$select=${nameOf<IEvaluationQueryModel>(
      (e) => e.teamName
    )}, ${nameOf<IEvaluationQueryModel>(
      (e) => e.evaluateePosition
    )}, ${nameOf<IEvaluationQueryModel>((e) => e.evaluationName)}, ${nameOf<IEvaluationQueryModel>(
      (e) => e.evaluationResponseDate
    )}`;
    const expand = `$expand=${nameOf<IEvaluationQueryModel>((e) => e.responses)}`;
    return `query/evaluationMaxResult?${filter}&${select}&${expand}&$count=true`;
  }

  getPerson = (personId: string, ok404: boolean = true): Promise<Person> =>
    new Promise((resolve, reject) => {
      this.get(this.usersBaseUrl(personId), ok404, ApiVersions.v1_0)
        .pipe(map(this.convertPerson))
        .subscribe((data) => {
          if (data) {
            this.person.next(data);
            resolve(data);
          } else {
            const error = { message: 'Person not found', status: 404 };
            reject(reject);
            this.person.error(error);
          }
        });
    });

  getForms(userId: string) {
    this.get(this.getApplicationsUrl(userId), true)
      .pipe(map(this.convertApplications))
      .subscribe((data) => {
        if (data) {
          this.forms.next(data);
        }
      });
  }

  getPersonTeamHistory(personId: string) {
    return this.get(this.getPersonTeamHistoryUrl(personId), true, ApiVersions.v1_0)
      .pipe(map(this.convertTeamHistoryData))
      .subscribe((data) => {
        if (data) {
          this.personTeamHistory.next(data);
        }
      });
  }

  getEvaluationsSummary(userId: string) {
    this.get(this.getEvaluationsSummaryUrl(userId), true)
      .pipe(map(this.convertEvaluationsSummaryData))
      .subscribe((data) => {
        if (data) {
          this.personEvaluationSummary.next(data);
        }
      });
  }

  getTeamEvaluationResponsesAggregate(userId: string, teamId: string) {
    this.get(
      this.getTeamEvaluationResponsesDetailUrl(userId, teamId),
      true,
      ApiVersions.unversioned
    )
      .pipe(map(this.convertEvaluationResponseDetailData))
      .subscribe((result) => {
        if (result) {
          this.teamEvaluationResponsesAggregate.next(result);
        }
      });
  }
  getPersonEducation(userId: string) {
    this.get(this.getPersonEducationUrl(userId), true)
      .pipe(map(this.convertEducationData))
      .subscribe((data) => {
        if (data) {
          this.educations.next(data);
        }
      });
  }

  getPersonCertifications(userId: string) {
    this.get(this.getPersonCertificationsUrl(userId), false, ApiVersions.v1_0)
      .pipe(map(this.convertPersonCertificationsData))
      .pipe(map(this.orderPersonCertifications))
      .subscribe((data) => {
        if (data) {
          this.personCertifications.next(data);
        }
      });
  }

  patchPersonImageId(userId: string, imageId: string) {
    this.patch(this.getPersonImageUrl(userId), { imageId: imageId }).subscribe(() => {
      this.person.value.image = new Image(imageId);
    });
  }

  setStatus(userId: string, status: string) {
    let path = `${this.usersBaseUrl(userId)}/status?status=${status}`;
    this.patch(path).subscribe((result) => {
      if (result) {
        this.person.value.status = status;
        this.personStatus.next(status);
      }
    });
  }

  getEvaluationResponsesDetail(
    userId: string,
    evaluationFormId: string
  ): Observable<PersonEvaluationResponsesDetail> {
    return this.get(
      this.getEvaluationResponsesDetailUrl(userId, evaluationFormId),
      true,
      ApiVersions.unversioned
    ).pipe(map(this.convertEvaluationResponseDetailData));
  }

  getCertifications(): Observable<Certification[]> {
    return this.get(this.baseCertificationsPath, true)
      .pipe(map(this.convertCertificationsData))
      .pipe(map(this.orderCertifications));
  }

  savePersonCertification(
    userId: string,
    personCertificationId: string,
    personCertification: PersonCertificationWrite
  ): Observable<PersonCertification> {
    return personCertificationId
      ? this.updatePersonCertification(userId, personCertificationId, personCertification)
      : this.createPersonCertification(userId, personCertification);
  }

  patchPersonMetadata(personId: string, extSysId: string, successMessage: string) {
    let path = `${this.usersBaseUrl(personId)}/metadata`;
    this.patch(path, { externalSystemId: extSysId }, ApiVersions.v1_0).subscribe((result) => {
      if (result) {
        this.person.value.metadata = result.metadata;
        this.person.next(this.person.value);
        this.openSnackBar(successMessage, true);
      }
    });
  }

  private convertCertificationsData(data) {
    return data.map((certification) => new Certification(certification));
  }

  private orderCertifications(data) {
    return data.sort(StringIgnoreCaseComparator('name'));
  }

  private createPersonCertification(
    userId: string,
    personCertification: PersonCertificationWrite
  ): Observable<PersonCertification> {
    let body = JSON.stringify(personCertification);
    return this.post(this.getPersonCertificationsUrl(userId), body, null, ApiVersions.v1_0).pipe(
      map(this.convertPersonCertificationData)
    );
  }

  private updatePersonCertification(
    userId: string,
    personCertificationId: string,
    personCertification: PersonCertificationWrite
  ): Observable<PersonCertification> {
    let body = JSON.stringify(personCertification);
    return this.put(
      this.getPersonCertificationEditDeleteUrl(userId, personCertificationId),
      body,
      null,
      ApiVersions.v1_0
    ).pipe(map(this.convertPersonCertificationData));
  }

  private getPersonCertificationEditDeleteUrl(userId: string, certificationId: string) {
    return this.getPersonCertificationsUrl(userId).concat(`/${certificationId}`);
  }

  private convertPersonCertificationData(data) {
    return new PersonCertification(data);
  }

  patchRating(userId: string) {
    return lastValueFrom(this.patch(this.getPersonRatingUrl(userId)));
  }

  deletePerson(person: Person) {
    this.delete(this.usersBaseUrl(person.id), null, ApiVersions.v0_1, true).subscribe(
      () => {
        this.openSnackBar(`Successfully deleted ${person.fullName}.`, true);
      },
      (error) => {
        if (error?.status == 400 && error?.error) {
          this.openSnackBar(`${error.error}`);
        } else {
          this.openSnackBar(`Could not delete person.`);
        }
      }
    );
  }

  sendForm(userId: string, formId: string): Observable<any> {
    return this.post(
      `${this.baseUsersPath}/${userId}/applications/${formId}/assign`,
      null,
      null,
      ApiVersions.v1_0
    );
  }

  getPersonWorkspaceRatingAggregate(userId: string, workspaceId: string): Observable<number> {
    var url = this.getEvaluationsSummaryUrl(userId)
      .concat(`/${workspaceId}`)
      .concat('/workspace-rating');
    return this.get(url, true);
  }

  deleteForm(userId: string, applicationId: string): Observable<any> {
    return this.delete(
      `${this.baseUsersPath}/${userId}/applications/${applicationId}`,
      null,
      ApiVersions.v0_1,
      true
    );
  }
}
